import React, { useState } from 'react';
import { B, AI_COLOR, AI_LIGHT } from '../../context/ThemeContext';
import { Pill, AIBadge, Btn, Divider, Field, Card, SectionTitle } from './UIComponents';

export default function ResidentModal({ resident: r, aiMode, onClose }) {
  const [tab, setTab] = useState('overview');
  if (!r) return null;

  const tabs = ['overview', 'bsp', 'isp', 'cip', 'medications', 'activity'];
  const statusColor = { Given: B.green, Due: '#dc2626', Scheduled: B.teal, PRN: '#d97706' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }} onClick={onClose}>
      <div style={{
        background: B.white, borderRadius: 16, width: '100%', maxWidth: 760,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg,${B.navy},#1e5fa8)`, padding: '18px 22px',
          display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0
        }}>
          <div style={{
            width: 50, height: 50, borderRadius: '50%',
            background: `linear-gradient(135deg,${B.green},${B.teal})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0
          }}>{r.name.split(' ').map(x => x[0]).join('')}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Room {r.room} · Age {r.age} · {r.gender} · Admitted {r.admission}
            </div>
          </div>
          {aiMode && <AIBadge label="AI Enhanced" />}
          <Pill c={B.green}>{r.status}</Pill>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', fontSize: 16, fontFamily: 'inherit'
          }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 2, padding: '10px 22px 0', borderBottom: `1px solid ${B.border}`,
          background: B.white, flexShrink: 0, overflowX: 'auto'
        }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              fontFamily: 'inherit', fontSize: 11, fontWeight: 700, padding: '6px 14px',
              borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
              background: tab === t ? B.bg : 'transparent',
              color: tab === t ? B.navy : B.muted,
              borderBottom: tab === t ? `2px solid ${B.navy}` : '2px solid transparent',
              textTransform: 'capitalize', whiteSpace: 'nowrap'
            }}>{t === 'emar' ? 'eMAR' : t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>
          {tab === 'overview' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <Field label="Primary Diagnosis" value={r.dx} ai={aiMode} />
                  <Field label="Primary Care Physician" value={r.pcp} />
                  <Field label="Insurance" value={r.insurance} />
                </div>
                <div>
                  <Field label="Date of Birth" value={r.dob} />
                  <Field label="Emergency Contact" value={r.emergency} />
                  <Field label="Room" value={`Room ${r.room}`} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { l: 'BSP', c: AI_COLOR }, { l: 'ISP', c: B.blue },
                  { l: 'CIP', c: B.teal }, { l: `${r.meds.length} Meds`, c: B.green }
                ].map((b, i) => (
                  <span key={i} style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                    background: `${b.c}10`, color: b.c, border: `1px solid ${b.c}20`
                  }}>{b.l}</span>
                ))}
              </div>
            </div>
          )}

          {tab === 'bsp' && (
            <div>
              {aiMode && <div style={{ background: AI_LIGHT, border: `1px solid ${AI_COLOR}20`, borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                <AIBadge label="AI-Generated BSP Summary" />
                <div style={{ fontSize: 11, color: AI_COLOR, marginTop: 6 }}>Behavioral Support Plan auto-generated from care notes and incident history.</div>
              </div>}
              <Field label="Goals" value={r.bsp.goals} ai={aiMode} />
              <Field label="Known Triggers" value={r.bsp.triggers} />
              <Field label="Interventions" value={r.bsp.interventions} />
            </div>
          )}

          {tab === 'isp' && (
            <div>
              <Field label="Current Status Summary" value={r.isp.summary} ai={aiMode} />
              <Field label="Care Goals" value={r.isp.goals} />
              <Field label="Services & Frequency" value={r.isp.services} />
            </div>
          )}

          {tab === 'cip' && (
            <div>
              <Field label="Daily Care Plan" value={r.cip.plan} ai={aiMode} />
              <Field label="Staff Notes & Preferences" value={r.cip.notes} />
              <Field label="Dietary Requirements" value={r.cip.diet} />
            </div>
          )}

          {tab === 'medications' && (
            <div>
              <SectionTitle>Active Medications</SectionTitle>
              {r.meds.map((m, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderRadius: 10, border: `1px solid ${B.border}`,
                  background: m.status === 'Due' ? '#fff5f5' : B.bg, marginBottom: 8
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `${statusColor[m.status] || B.muted}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0
                  }}>💊</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: B.navy }}>{m.name}</div>
                    <div style={{ fontSize: 10, color: B.muted }}>{m.route} · {m.freq}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: m.status === 'Due' ? '#dc2626' : B.text }}>{m.next}</div>
                    <Pill c={statusColor[m.status] || B.muted}>{m.status}</Pill>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'activity' && (
            <div>
              <SectionTitle>Recent Activity Log {aiMode && <AIBadge label="AI Summarized" />}</SectionTitle>
              {r.activity.map((a, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, padding: '10px 0',
                  borderBottom: i < r.activity.length - 1 ? `1px solid ${B.border}` : 'none'
                }}>
                  <Pill c={a.type === 'Incident' ? '#dc2626' : a.type === 'Behavior' ? AI_COLOR : B.teal}>{a.type}</Pill>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: B.text }}>{a.note}</div>
                    <div style={{ fontSize: 10, color: B.muted, marginTop: 3 }}>{a.date} · {a.staff}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${B.border}`, display: 'flex', gap: 8, flexShrink: 0 }}>
          <Btn onClick={onClose} outline color={B.muted}>Close</Btn>
          <Btn color={B.blue}>Edit Profile</Btn>
          {aiMode && <Btn style={{ background: `linear-gradient(135deg,${AI_COLOR},#6d28d9)` }}>⚡ AI Care Summary</Btn>}
        </div>
      </div>
    </div>
  );
}
